import {
  observable,
  computed,
  action,
  extendObservable,
  isComputedProp,
  isObservableProp,
  isObservableArray,
} from 'mobx';

import { AppRegistry } from 'core/registry';
import { modelAppTransformer, getMemoisationID } from 'core/utils';
import { Model } from 'core/model';
import RefMap from 'libs/RefMap';
import { isObject } from 'utils';

export function AppFactory(parent, appType) {
  if (typeof appType === 'string') {
    // eslint-disable-next-line no-param-reassign
    appType = AppRegistry.get(appType);
  }
  // create a parent memoId so that we can keep this App in view across multiple property accesses.
  // BUT NOT load the same app with multiple parents. Each AppManager will now have it's own App 'context'.
  // This makes it possible to reliably call this.manager from an App again (and get the right AppManager)
  // - though this should be discouraged for other reasons of de-coupling

  return modelAppTransformer(
    upstream => {
      // eslint-disable-next-line new-cap
      const app = new appType(parent, undefined, true);
      app.$cacheContextId = parent.$cacheContextId;
      app.setUpstream(upstream);
      if (app.init) {
        app.init();
      }
      return app;
    },
    () => parent.$cacheContextId,
  );
}

export function AppRef(obj, propName, AppCls) {
  // Requires that obj has an observable map .models - i.e. AppManager.

  // set the memoisation on the AppManager - which we'll pass down to ensure that
  // a) model-app transformations are namespaced by the AppManger instance
  //    => This ensures that App.parent won't be overriden.
  // b) If the same model-app is loaded twice by the same AppManager, it will be the same App
  const cacheContextId = getMemoisationID(obj, '$cacheContextId');

  const AppConstructor = AppFactory(obj, AppCls);
  extendObservable(obj, {
    get [propName]() {
      const localApp = this.localApps.get(propName);
      if (localApp) {
        return localApp;
      }
      const model = this.models.get(propName);
      if (model === undefined || model === null) {
        return null;
      }
      const app = AppConstructor(model).get();
      app.$cacheContextId = cacheContextId;
      return app;
    },
    set [propName](modelInst) {
      // When setting to null its ambiguous .localApps.set() or .models.set()
      // This is only done explicitly when we set an App, here we use a
      // predefined symbol. App.constructor.Null
      // eslint-disable-next-line no-use-before-define
      if (modelInst instanceof AppBase) {
        this.localApps.set(propName, modelInst); // AppInst not from upstream
      } else if (
        modelInst instanceof Model ||
        isObservableArray(modelInst) ||
        modelInst === null
      ) {
        this.models.set(propName, modelInst);
        // TODO: only remove localApps('propName') if the mutationID/localUUID
        // returned is the one associated with the current localApp?
        // similar logic needed when setting the SubApps
        this.localApps.delete(propName);
      } else if (
        typeof modelInst === 'symbol' &&
        Symbol.keyFor(modelInst) === 'AppNull'
      ) {
        this.localApps.set(propName, null);
      } else {
        throw new Error(
          `Setting AppRef '${propName}' on '${obj.constructor.name}' to invalid value ${modelInst}`,
        );
      }
    },
  });
}
export class AppTreeBase {
  disposers = [];
  _manager = null;

  constructor(parent) {
    this.parent = parent;
  }

  @computed get manager() {
    if (this._manager) return this._manager;
    // Return the next manager up the tree from an AppManager or App
    if (!this.parent) {
      // return this?
      return null;
    }
    const parentClassName = this.parent.constructor.registryName || '';
    // use  'instanceOf AppManager' -- avoid circular import - we should not rely on registryName
    if (parentClassName.includes('Manager')) {
      return this.parent;
    }
    return this.parent.manager;
  }

  setManager(manager) {
    this._manager = manager;
  }
}
export class AppBase extends AppTreeBase {
  locals = observable.map();

  isPropertyDirty(key) {
    return this.locals.has(key);
  }

  @computed get isDirty() {
    return this.locals.size > 0;
  }

  dispose = () => {
    // TODO: if we ever actually dispose an app we should dispose the tree of apps below it
    // eslint-disable-next-line no-restricted-syntax
    for (const disposer of this.disposers) {
      disposer();
    }
  };

  init() {
    // no-op fallback
  }
}

export const Empty = value => {
  if (!value || (isObservableArray(value) && value?.length === 0)) return true;

  return false;
};

export class App extends AppBase {
  static registryName = 'App';
  @observable upstream;

  static Null = Symbol.for('AppNull');

  constructor(
    parent = null,
    initial = {},
    fromUpstream = false,
    shouldExpandValues = true,
  ) {
    super(parent);
    this.appProps = this.constructor.AppProps;
    this._initial = initial;
    this._fromUpstream = fromUpstream;

    if (shouldExpandValues) {
      this.expandValues();
    }
  }

  expandValues = (updateIdCache = undefined) => {
    const updateCache = updateIdCache || {};
    const model = this.constructor.Model;

    const defaultValues = {};
    // should model always be true? invariant?
    if (model) {
      // eslint-disable-next-line new-cap
      const m = new model();
      // eslint-disable-next-line no-restricted-syntax
      for (const prop of this._getAppPropertyNames(model, true)) {
        if (!(isObservableProp(this, prop) && isObservableProp(m, prop))) {
          // Where App explicitly override an observable property on Model
          // => don't add a model prop
          this._addModelProperty(prop);
        }
        // If the app isn't from upstream, write the model defaults to the localValues
        if (!this._fromUpstream) {
          // model.prototype[key] gets changed at some point! So can't access property initialiser defaults
          if (isObservableProp(m, prop) && isComputedProp(m, prop)) {
            // eslint-disable-next-line no-continue
            continue;
          }
          defaultValues[prop] = m[prop];
        }
      }
    }
    if (!this._fromUpstream) {
      // eslint-disable-next-line no-restricted-syntax
      for (const prop of Object.keys(this._initial)) {
        defaultValues[prop] = this._initial[prop];
      }
      this.update(defaultValues, updateCache);
    }
    this._modelTransformers = {};
    if (this.appProps) {
      // eslint-disable-next-line no-restricted-syntax
      for (const prop of Object.keys(this.appProps)) {
        this._modelTransformers[prop] = AppFactory(this, this.appProps[prop]);
      }
    }
  };

  update = (properties, updateIdCache = undefined) => {
    const { appProps } = this;
    const updateCache = updateIdCache || {};
    // eslint-disable-next-line no-restricted-syntax
    for (const prop of Object.keys(properties)) {
      const value = properties[prop];
      if (appProps && appProps[prop]) {
        if (!Empty(value)) {
          const appType =
            typeof appProps[prop] === 'string'
              ? AppRegistry.get(appProps[prop])
              : appProps[prop];
          if (this[prop] instanceof AppBase) {
            this[prop].update(value, updateCache);
          } else if (
            typeof appProps[prop] === 'string' &&
            appProps[prop] === value.constructor.registryName
          ) {
            this[prop] = value;
          } else if (value instanceof appType) {
            this[prop] = value;
          } else {
            const propId = value.id;
            const cachedUpdatedApp = updateCache[propId];
            if (cachedUpdatedApp) {
              this[prop] = cachedUpdatedApp;
              // eslint-disable-next-line no-continue
              continue;
            }

            // eslint-disable-next-line new-cap
            const newApp = new appType(this, value, false, false);

            if (propId) updateCache[propId] = newApp;

            // is instanceof App or ArrayApp
            if (newApp.expandValues) {
              newApp.expandValues(updateCache);
            }
            this[prop] = newApp;
          }
        }
      } else {
        this[prop] = value;
      }
    }
  };

  @action setUpstream = upstream => {
    if (!(upstream instanceof Model)) {
      // eslint-disable-next-line no-throw-literal
      throw `upstream should always be a Model got ${upstream.constructor.name} for ${this.constructor.name}`;
    }
    this.upstream = upstream;
  };

  @action ResetLocals = (props, resetIdCache = undefined) => {
    const resetCache = resetIdCache || {};
    if (resetCache[this.id]) return;
    let appProps = [];
    if (this.constructor.AppProps) {
      appProps = Object.keys(this.constructor.AppProps);
    }
    if (props === undefined) {
      this.locals.clear();
      if (this.id) {
        resetCache[this.id] = this;
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const prop of appProps) {
        if (this[prop]) {
          // check recursive dirtiness?
          this[prop].ResetLocals(undefined, resetCache);
        }
      }
    } else {
      if (this.id) {
        resetCache[this.id] = this;
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const prop of props) {
        this.locals.delete(prop);
        if (appProps.includes(prop)) {
          this[prop].ResetLocals(undefined, resetCache);
        }
      }
    }
  };

  // on loadData (query.result changed) ResetLocals if? mutation version number
  _addModelProperty(prop) {
    let getter;
    if (
      this.constructor.AppProps &&
      Object.keys(this.constructor.AppProps).includes(prop)
    ) {
      getter = () => {
        if (this.isPropertyDirty(prop)) {
          // if locals.get(prop) is a model, we should still do this._modelTransformers as below
          return this.locals.get(prop);
        }
        if (this.upstream !== undefined) {
          if (
            this.upstream[prop] === null ||
            this.upstream[prop] === undefined
          ) {
            return null;
          }
          const app = this._modelTransformers[prop](this.upstream[prop]).get();
          return app;
        }
        return undefined;
      };
    } else {
      getter = () => {
        if (this.isPropertyDirty(prop)) {
          return this.locals.get(prop);
        }
        if (this.upstream !== undefined) {
          return this.upstream[prop];
        }
        return undefined;
      };
    }
    // should/can these properties be defined on the class prototype?
    Object.defineProperty(this, prop, {
      enumerable: true,
      configurable: true,
      get: getter,
      set: action(value => {
        if (
          this.isPropertyDirty(prop) ||
          !this.upstream ||
          value !== this.upstream[prop]
        ) {
          this.locals.set(prop, value);
        }
      }),
    });
  }

  _getAppPropertyNames(model, appProps = false) {
    // eslint-disable-next-line new-cap
    const dummy = new model();
    const propNames = this._getAllPropertyNames(dummy).filter(prop => {
      if (
        prop.startsWith('_') ||
        ['constructor', 'loadData', 'toJs'].includes(prop) ||
        (!appProps && this.appProps && this.appProps[prop] !== undefined)
      ) {
        return false;
      }
      return true;
    });
    return propNames;
  }

  _getAllPropertyNames(dummy) {
    const prototype = Object.getPrototypeOf(dummy);
    if (prototype) {
      return Object.getOwnPropertyNames(dummy).concat(
        this._getAllPropertyNames(prototype),
      );
    }
    return [];
  }

  _valueToJs(
    jsCacheMap = new RefMap(),
    value,
    shallow = false,
    stripDownCircularRefs = false,
  ) {
    // NOTE(david): It is reasonable to check jsCacheMap here, unnecessary at this point with the shallow flag.
    // NOTE(david): The shallow flag is just a safeguard mechanism that doesn't break anything
    //              for the data shapes we expect.
    //              It can be safely removed for deeper recursion of the observable object case.
    if (!value) return value;
    if (value instanceof Model) {
      return value.toJs(undefined, stripDownCircularRefs);
    }
    if (value instanceof App) {
      return value.toJs(jsCacheMap, stripDownCircularRefs);
    }
    // value is ArrayApp
    if (value?.ElementApp?.prototype instanceof App) {
      return value.toJs(jsCacheMap, stripDownCircularRefs);
    }
    // value is an observable object that contains keys whose values are Apps, ArrayApps or Models
    if (isObject(value) && !shallow) {
      const obj = {};
      Object.keys(value).forEach(key => {
        obj[key] = this._valueToJs(
          jsCacheMap,
          value[key],
          true, // shallow
          stripDownCircularRefs,
        );
      });
      return obj;
    }

    // TODO (david): There's a hypothetical case were value is an observable array whose items are one of the
    // other types. The implementation would mirror the observable object block.

    // value is a primitive (or an unhandled case)
    return value;
  }

  toJs(jsCacheMap = new RefMap(), stripDownCircularRefs = false) {
    if (jsCacheMap.has(this)) {
      const cached = jsCacheMap.get(this);
      if (stripDownCircularRefs) {
        if (cached.id) {
          return { id: cached.id };
        }
        return null;
      }
      return cached;
    }
    let result = {};
    jsCacheMap.set(this, result);

    // 1. First take the upstream Model and make it simple JS.
    if (this.upstream && this.upstream instanceof Model) {
      result = this.upstream.toJs(undefined, stripDownCircularRefs);
    }

    // 2. Then add/override to it all the locals
    const localKeys = [...this.locals.keys()];
    localKeys.forEach(key => {
      // Check that locals[key] is really what is being resolved when you access app[key]
      if (this.locals.get(key) === this[key]) {
        const jsValue = this._valueToJs(
          jsCacheMap,
          this[key],
          false,
          stripDownCircularRefs,
        );
        if (jsValue !== undefined) {
          result[key] = jsValue;
        }
      }
    });

    // 3. Then add the observable props of this instance (that are not 'upstream')
    // These are dynamic observables added after the initial construction so they are not in this.locals
    // For example: jobSpec.valuesTarget
    const observableKeys = Object.keys(this).filter(
      key => isObservableProp(this, key) && key !== 'upstream',
    );
    observableKeys.forEach(key => {
      const jsValue = this._valueToJs(
        jsCacheMap,
        this[key],
        false,
        stripDownCircularRefs,
      );
      if (jsValue !== undefined) {
        result[key] = jsValue;
      }
    });

    return result;
  }
}
